import { IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { animate } from "framer-motion";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { useResizeDetector } from "react-resize-detector";
import { MediaKey } from "../shared/Img";
import { ReferenceImage } from "../shared/ReferenceImage";

export const DomScale = ({
    scale = 60,
    children,
    className,
    childrenClassName = "h-fit",
}: {
    scale?: number;
    children: ReactNode;
    className: string;
    childrenClassName?: string;
}) => {
    const refC = useRef<HTMLDivElement>(null);
    const { ref } = useResizeDetector<HTMLDivElement>({
        onResize: () => {
            const containerHeight = ref.current?.getBoundingClientRect().height;
            if (!refC.current) return;
            if (!containerHeight) return;
            refC.current.style.height = containerHeight + "px";
        },
    });
    const width = (100 / scale) * 100;
    return (
        <>
            <div ref={refC} className={`${className} `}>
                <div
                    style={{
                        transform: `scale(${scale / 100}, ${scale / 100})`,
                        WebkitTransform: `scale(${scale / 100}, ${scale / 100})`,
                        width: `${width}%`,
                        marginLeft: `-${(width - 100) / 2}%`,
                        marginRight: `-${(width - 100) / 2}%`,
                    }}
                    className="origin-top will-change-transform"
                >
                    <div className={childrenClassName} ref={ref}>
                        {children}
                    </div>
                </div>
            </div>
        </>
    );
};

export const Bullet = (props: {
    className?: string;
    titleClassName?: string;
    number?: string;
    icon?: IconDefinition;
    title?: string;
    subtitle?: string;
    twIconClassName?: string;
}) => {
    return (
        <div className={clsx("flex gap-2", props.className)}>
            {props.icon && (
                <FontAwesomeIcon
                    icon={props.icon}
                    className={clsx("text-reonic-green text-sm mt-[3px]", props.twIconClassName)}
                />
            )}
            {props.number && (
                <div
                    className={clsx(
                        "bg-reonic-green shrink-0 text-white text-xs flex items-center justify-center font-semibold w-5 h-5 rounded-full mt-[2px]",
                        props.twIconClassName,
                    )}
                >
                    {props.number}
                </div>
            )}
            <div>
                <div className={clsx(" sm:text-md text-sm sm:leading-5", props.titleClassName)}>{props.title}</div>
                {props.subtitle && <div className="text-zinc-500 font-normal text-xs -mt-0.5">{props.subtitle}</div>}
            </div>
        </div>
    );
};

export const Slide = ({
    nr: _nr,
    delay,
    twColor,
    initial,
    show: _show,
    children,
}: {
    nr: number;
    delay: number;
    twColor?: string;
    initial?: boolean;
    show: boolean | null;
    children: [ReactNode, ReactNode];
}) => {
    // const [show, setShow] = useState<"animation-show" | "animation-hide">()

    const show = _show === true ? "animation-show" : _show === false ? "animation-hide" : "animation-none";

    return (
        <>
            <div
                className={`
                    h-full w-full row-start-1 col-start-1 pt-[100px] pb-[50px] lg:pb-0 relative z-10
                `}
            >
                <div
                    className={`
                        w-full h-full
                        grid grid-cols-1 xs:grid-cols-[250px_1fr] sm:grid-cols-[350px_1fr]  md:grid-cols-[550px_1fr] 
                        gap-y-5 xs:gap-x-5 md:gap-x-10
                    `}
                >
                    <div
                        // transition-all duration-[200ms] delay-[700ms]
                        className={`
                            order-3 xs:order-1
                            relative w-full shrink-0
                            flex flex-col sm:justify-center xs:pb-[10px] md:pb-[50px]
                            ${
                                show === "animation-show" ? "opacity-100" : "opacity-0"
                            } transition-all duration-300 delay-[600ms]
                        `}
                    >
                        {children[0]}
                    </div>
                    <div
                        // duration-[300ms] delay-[700ms]
                        className={`
                            shrink-0 grow-0 flex flex-col 
                            order-2 pr-[42px] xs:pt-[10px] sm:pt-[20px] md:pt-[80px]
                            items-start xs:items-end max-h-[250px] sm:max-h-full
                            ${
                                show === "animation-show" ? "opacity-100" : "opacity-0"
                            } transition-all duration-200 delay-300
                        `}
                    >
                        {children[1]}
                    </div>
                </div>
            </div>

            {/* Background */}
            <div
                // bottom-[250px] xs:bottom-[340px] md:bottom-[300px]
                className={` 
                        absolute left-0 
                        top-[-200px] h-[825px] xs:h-[600px] sm:h-[875px] lg:h-[800px]
                        w-full flex flex-col items-center justify-end will-change-auto
                        group ${initial ? "animation-initial" : "animation-later"} ${show}
                    `}
            >
                <div
                    className={`
                        shrink-0 transition-all w-[calc(100%+100px)] h-full rotate-[-5deg] relative 
                        ${initial && "bg-zinc-100"} 
                    `}
                >
                    <div className="absolute left-0 top-0 h-full w-full max-w-full overflow-hidden">
                        <div className="w-full h-[200%] rotate-[5deg] -mt-[200px]  flex group-[.animation-hide]:justify-end will-change-auto">
                            <div className="will-change-transform transform-gpu bg-white w-full  max-w-[0px] group-[.animation-show]:max-w-[100%] delay-[300ms] group-[.animation-hide]:delay-[0ms] duration-[2000ms] group-[.animation-hide]:duration-[3000ms] transition-all h-[2000px]">
                                <div className={`w-full h-full ${twColor}`} />
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    className={`
                        shrink-0 transition-all w-[calc(100%+100px)] h-[65px] rotate-[-5deg] relative 
                        ${initial && "bg-zinc-100"} 
                    `}
                >
                    <div className="absolute left-0 top-0 h-full w-full max-w-full overflow-hidden">
                        <div className="w-full h-[200%] rotate-[5deg] -mt-[200px] flex group-[.animation-hide]:justify-end will-change-auto">
                            <div className="will-change-transform transform-gpu bg-white w-full max-w-[0px] group-[.animation-show]:max-w-[100%] delay-[200ms] group-[.animation-hide]:delay-[100ms] duration-[2000ms] group-[.animation-hide]:duration-[3000ms] transition-all h-[2000px]">
                                <div className={`w-full h-full ${twColor} bg-opacity-75`} />
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    className={`
                        shrink-0 transition-all w-[calc(100%+100px)] h-[27px] rotate-[-5deg] relative 
                        ${initial && "bg-zinc-100"} 
                    `}
                >
                    <div className="absolute left-0 top-0 h-full w-full max-w-full overflow-hidden">
                        <div className="w-full h-[200%] rotate-[5deg] -mt-[200px] flex group-[.animation-hide]:justify-end will-change-auto">
                            <div className="will-change-transform transform-gpu bg-white w-full max-w-[0px] group-[.animation-show]:max-w-[100%] delay-[100ms] group-[.animation-hide]:delay-[200ms] duration-[2000ms] group-[.animation-hide]:duration-[3000ms] transition-all h-[2000px]">
                                <div className={`w-full h-full ${twColor} bg-opacity-50`} />
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    className={`
                        shrink-0 transition-all w-[calc(100%+100px)] h-[9px] rotate-[-5deg] relative 
                        ${initial && "bg-zinc-100"} 
                    `}
                >
                    <div className="absolute left-0 top-0 h-full w-full max-w-full overflow-hidden">
                        <div className="w-full h-[200%] rotate-[5deg] -mt-[200px] flex group-[.animation-hide]:justify-end will-change-auto">
                            <div className="will-change-transform transform-gpu bg-white w-full max-w-[0px] group-[.animation-show]:max-w-[100%] delay-[000ms] group-[.animation-hide]:delay-[300ms] duration-[2000ms] group-[.animation-hide]:duration-[3000ms] transition-all h-[2000px]">
                                <div className={`w-full h-full ${twColor} bg-opacity-25`} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export const Thumb = ({ twColor, selected, onClick }: { selected: boolean; onClick: () => void; twColor: string }) => {
    return (
        <button onClick={() => onClick()} className="h-6" aria-label="thumb">
            <div
                className={`
                transition-all rounded-full h-[7px] duration-300
                ${selected ? `w-[60px] ${twColor}` : `w-[20px] bg-zinc-300`}
            `}
            />
        </button>
    );
};

export const Customers = () => {
    const customers: MediaKey[] = [
        "/pages/de-de/industry/dealerships/references/bad_nauheim.png",
        "/pages/de-de/industry/solar/references/bhag_logo.png",
        "/pages/de-de/industry/solar/references/emevo_logo.png",
        "/pages/de-de/industry/dealerships/references/energieschwaben.png",
        "/pages/de-de/industry/dealerships/references/enwor.svg",
        "/pages/de-de/industry/dealerships/references/evi_hildesheim.svg",
        "/pages/de-de/industry/dealerships/references/evm.svg",
        "/pages/de-de/industry/dealerships/references/ewv.svg",
        "/pages/de-de/industry/dealerships/references/heidelberg.png",
        "/pages/de-de/industry/solar/references/kreuzmayr_logo.png",
        "/pages/de-de/industry/dealerships/references/eigenstrom.png",
        "/pages/de-de/industry/solar/references/nvb_logo.png",
        "/mirror/industry/solar/assets/CI_Solarbau+ en.png",
        "/pages/de-de/industry/solar/references/pvgreen-logo.svg",
        "/pages/de-de/industry/solar/references/gasag.png",
        "/pages/de-de/industry/solar/references/New_logo.svg.png",
        "/pages/de-de/industry/solar/references/svea_logo.png",
        "/pages/de-de/industry/dealerships/references/raiffeisen.png",
        "/pages/de-de/industry/dealerships/references/smart-solar.png",
        "/pages/de-de/industry/dealerships/references/solarwerft.png",
        "/pages/de-de/industry/dealerships/references/sungrade.png",
        "/pages/de-de/industry/dealerships/references/sw_borken.svg",
        "/pages/de-de/industry/dealerships/references/sw_coesfeld.svg",
        "/pages/de-de/industry/dealerships/references/sw_geldern.svg",
        "/pages/de-de/industry/dealerships/references/sw_guetersloh.svg",
        "/pages/de-de/industry/dealerships/references/sw_hameln.svg",
        "/pages/de-de/industry/dealerships/references/sw_hamm.png",
        "/pages/de-de/industry/dealerships/references/sw_herne.png",
        "/pages/de-de/industry/solar/references/sw_potsdam_logo.png",
        "/pages/de-de/industry/dealerships/references/sw_rheine.png",
        "/pages/de-de/industry/dealerships/references/sw_strassfurt.svg",
        "/pages/de-de/industry/dealerships/references/sw-luebbecke.svg",
        "/pages/de-de/industry/dealerships/references/unna.svg",
        "/pages/de-de/industry/dealerships/references/vwew.png",
        "/pages/de-de/industry/dealerships/references/wvv.png",
        "/pages/de-de/industry/solar/references/DAWA.png",
        "/pages/de-de/industry/solar/references/biberphotovoltaik.png",
        "/pages/de-de/industry/dealerships/references/lichtblick.svg",
        "/pages/de-de/industry/dealerships/references/invanova.png",
        "/pages/de-de/industry/dealerships/references/sunday_solar.png",
        "/pages/de-de/industry/dealerships/references/solar_profi.png",
        "/pages/de-de/industry/dealerships/references/asw.png",
        // "/pages/de-de/industry/dealerships/references/auto-boederker.png",
        // "/pages/de-de/industry/dealerships/references/auto-grill.svg",
        // "/pages/de-de/industry/dealerships/references/auto-haeuser.svg",
        // "/pages/de-de/industry/dealerships/references/auto-heinen.png",
        // "/pages/de-de/industry/dealerships/references/auto-mueller-online.svg",
        // "/pages/de-de/industry/dealerships/references/auto-weller.svg",
        // "/pages/de-de/industry/dealerships/references/autohaus-ebersberg.png",
        // "/pages/de-de/industry/dealerships/references/autohaus-gruenhagen.webp",
        // "/pages/de-de/industry/dealerships/references/autohaus-stiglmayr.png",
        // "/pages/de-de/industry/dealerships/references/autohaus-weil.png",
        // "/pages/de-de/industry/dealerships/references/autohaus-wolfsburg.svg",
        "/pages/de-de/industry/dealerships/references/azf.png",
        "/pages/de-de/industry/dealerships/references/bad_nauheim.png",
        "/pages/de-de/industry/dealerships/references/badermainzl.png",
        "/pages/de-de/industry/dealerships/references/best.png",
        "/pages/de-de/industry/solar/references/bhag_logo.png",
        "/pages/de-de/industry/dealerships/references/bUndK.svg",
        "/pages/de-de/industry/dealerships/references/ehrhardt.png",
        "/pages/de-de/industry/dealerships/references/ehrlich.png",
        "/pages/de-de/industry/solar/references/emevo_logo.png",
        "/pages/de-de/industry/dealerships/references/energieschwaben.png",
        "/pages/de-de/industry/dealerships/references/enwor.svg",
        "/pages/de-de/industry/dealerships/references/epple.png",
        "/pages/de-de/industry/dealerships/references/evi_hildesheim.svg",
        "/pages/de-de/industry/dealerships/references/evm.svg",
        "/pages/de-de/industry/dealerships/references/ewv.svg",
        // "/pages/de-de/industry/dealerships/references/fettwirtz.png",
        "/pages/de-de/industry/dealerships/references/fischer.svg",
        "/pages/de-de/industry/dealerships/references/fricke.png",
        "/pages/de-de/industry/dealerships/references/heidelberg.png",
        "/pages/de-de/industry/dealerships/references/huelpert-automobile.png",
        "/pages/de-de/industry/dealerships/references/hw-gruppe.png",
        "/pages/de-de/industry/dealerships/references/jacobs-gruppe.png",
        "/pages/de-de/industry/dealerships/references/kaesmann.png",
        // "/pages/de-de/industry/dealerships/references/kahle.png",
        "/pages/de-de/industry/dealerships/references/kimbeck.png",
        "/pages/de-de/industry/dealerships/references/krahenders.webp",
        "/pages/de-de/industry/solar/references/kreuzmayr_logo.png",
        // "/pages/de-de/industry/dealerships/references/lalinea.png",
        "/pages/de-de/industry/dealerships/references/lell.png",
        "/pages/de-de/industry/dealerships/references/loehr.png",
        "/pages/de-de/industry/dealerships/references/meuller.png",
        "/pages/de-de/industry/dealerships/references/eigenstrom.png",
        "/pages/de-de/industry/solar/references/nvb_logo.png",
        "/pages/de-de/industry/dealerships/references/ostermeier.png",
        "/pages/de-de/industry/dealerships/references/raiffeisen.png",
        "/pages/de-de/industry/dealerships/references/renzenbrink.svg",
        "/pages/de-de/industry/dealerships/references/rhein.svg",
        "/pages/de-de/industry/dealerships/references/rosier.png",
        "/pages/de-de/industry/dealerships/references/smart-solar.png",
        "/pages/de-de/industry/dealerships/references/solarwerft.png",
        "/pages/de-de/industry/dealerships/references/spreckelsen.png",
        "/pages/de-de/industry/dealerships/references/goenergy.png",
        "/pages/de-de/industry/dealerships/references/sternauto.png",
        "/pages/de-de/industry/dealerships/references/sungrade.png",
        "/pages/de-de/industry/dealerships/references/sw_bad_pyrmont.svg",
        "/pages/de-de/industry/dealerships/references/sw_borken.svg",
        "/pages/de-de/industry/dealerships/references/sw_coesfeld.svg",
        "/pages/de-de/industry/dealerships/references/sw_geldern.svg",
        "/pages/de-de/industry/dealerships/references/sw_guetersloh.svg",
        "/pages/de-de/industry/dealerships/references/sw_hameln.svg",
        "/pages/de-de/industry/dealerships/references/sw_hamm.png",
        "/pages/de-de/industry/dealerships/references/sw_herne.png",
        "/pages/de-de/industry/solar/references/sw_potsdam_logo.png",
        "/pages/de-de/industry/dealerships/references/sw_rheine.png",
        "/pages/de-de/industry/dealerships/references/sw_strassfurt.svg",
        "/pages/de-de/industry/dealerships/references/sw-luebbecke.svg",
        "/pages/de-de/industry/dealerships/references/thiel-gruppe.svg",
        "/pages/de-de/industry/dealerships/references/unna.svg",
        "/pages/de-de/industry/dealerships/references/vwew.png",
        // "/pages/de-de/industry/dealerships/references/vwz-roehr.png",
        // "/pages/de-de/industry/dealerships/references/weil.png",
        // "/pages/de-de/industry/dealerships/references/weller-gruppe.png",
        // "/pages/de-de/industry/dealerships/references/weller.png",
        "/pages/de-de/industry/dealerships/references/wvv.png",
        "/pages/de-de/industry/solar/references/DAWA.png",
        "/pages/de-de/industry/solar/references/biberphotovoltaik.png",
        "/pages/de-de/industry/dealerships/references/lichtblick.svg",
        "/pages/de-de/industry/dealerships/references/goenergy.png",
        "/pages/de-de/industry/dealerships/references/invanova.png",
        "/pages/de-de/industry/dealerships/references/sunday_solar.png",
        "/pages/de-de/industry/dealerships/references/solar_profi.png",
    ];

    return customers.map((m) => (
        <div className="px-5 h-14 flex items-center justify-center grayscale opacity-75 hover:opacity-100 hover:grayscale-0 transition-all overflow-hidden hover:bg-gray-200">
            <ReferenceImage noGrayscale alt="" twScale="" src={m} />
        </div>
    ));
};

export const Counter = ({
    from,
    to,
    digits,
    className,
}: {
    from: number;
    digits: number;
    to: number;
    className?: string;
}) => {
    const [number, setNumber] = useState<number>(from);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!ref.current) return;

        var observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    const controls = animate(from, to, {
                        duration: 3,
                        bounce: 1,
                        ease: [0.42, 0.0, 0.58, 1.0],
                        onUpdate(value) {
                            setNumber(Math.round(value));
                        },
                    });
                    return () => controls.stop();
                } else {
                    setNumber(0);
                }
            },
            {
                root: null, // default is the viewport
                threshold: 0.5, // percentage of target's visible area. Triggers "onIntersection"
            },
        );
        observer.observe(ref.current);

        return () => {
            observer.disconnect();
        };
    }, [from, to]);

    return (
        <div style={{ width: `${digits * 34}px` }} className={className} ref={ref}>
            {number}
        </div>
    );
};
